import { Check } from '@tamagui/lucide-icons-2'
import logo from 'images/logo.svg'
import { SizableText, View, XStack, YStack } from 'tamagui'

const LogoWhite = () => (
    <svg viewBox="0 0 1100 320" style={{ height: 48 }}>
        <g transform="translate(20, 10)">
            <rect x="0" y="0" width="300" height="300" rx="50" fill="#FFFFFF" />
            <circle cx="150" cy="120" r="56" fill="#0F9690" />
            <rect x="128" y="145" width="44" height="96" rx="7" fill="#0F9690" />
            <circle cx="150" cy="120" r="24" fill="#F47068" />
        </g>
        <g transform="translate(370, 160)">
            <text
                fontFamily="Helvetica, Arial, sans-serif"
                fontSize="170"
                fontWeight="300"
                fill="#FFFFFF"
                letterSpacing="2"
                dominantBaseline="central"
            >
                <tspan>Buyer</tspan>
                <tspan fontWeight="700">Flo</tspan>
            </text>
        </g>
    </svg>
)

interface SignupLayoutProps {
    children: React.ReactNode
    step: number
    totalSteps: number
}

const FEATURES = [
    'Purchaser tracking & management',
    'Automated workflows & notifications',
    'E-signing & document management',
    'Xero accounting integration',
]

export const SignupLayout = ({ children, step, totalSteps }: SignupLayoutProps) => {
    return (
        <XStack
            minHeight="100vh"
            width="100%"
        >
            {/* Left branding panel - hidden on small screens */}
            <YStack
                display="none"
                $md={{ display: 'flex' }}
                width="45%"
                backgroundColor="$primary"
                padding="$8"
                justifyContent="center"
                alignItems="center"
                gap="$6"
            >
                <LogoWhite />
                <SizableText
                    color="white"
                    fontSize="$9"
                    fontWeight="700"
                    textAlign="center"
                    maxWidth={400}
                    spacing={3}
                >
                    Streamline your buyer management
                </SizableText>
                <SizableText
                    color="rgba(255,255,255,0.8)"
                    fontSize="$5"
                    textAlign="center"
                    maxWidth={400}
                >
                    Join real estate agencies using BuyerFlo to manage clients, automate workflows, and stop
                    wasting time with boring admin.
                </SizableText>
                <YStack
                    gap="$3"
                    marginTop="$4"
                >
                    {FEATURES.map(text => (
                        <XStack
                            key={text}
                            gap="$3"
                            alignItems="center"
                        >
                            <View
                                width={24}
                                height={24}
                                borderRadius={12}
                                backgroundColor="rgba(255,255,255,0.2)"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Check
                                    size={14}
                                    color="white"
                                />
                            </View>
                            <SizableText
                                color="white"
                                fontSize="$4"
                            >
                                {text}
                            </SizableText>
                        </XStack>
                    ))}
                </YStack>
            </YStack>

            {/* Right form panel */}
            <YStack
                flex={1}
                justifyContent="center"
                alignItems="center"
                padding="$6"
                $sm={{ padding: '$4' }}
                backgroundColor="$background"
            >
                {/* Mobile-only logo */}
                <View
                    display="flex"
                    $md={{ display: 'none' }}
                    marginBottom="$4"
                >
                    <img
                        src={logo}
                        alt="BuyerFlo"
                        style={{ height: 40 }}
                    />
                </View>

                {/* Progress bar */}
                <XStack
                    gap="$2"
                    marginBottom="$6"
                    alignItems="center"
                    width="100%"
                    maxWidth={480}
                >
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <View
                            key={i}
                            height={4}
                            flex={1}
                            borderRadius={2}
                            backgroundColor={i < step ? '$primary' : '$gray5'}
                        />
                    ))}
                </XStack>

                {/* Form content */}
                <YStack
                    width="100%"
                    maxWidth={480}
                    gap="$4"
                >
                    {children}
                </YStack>
            </YStack>
        </XStack>
    )
}
